import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

export type BadgeTone = 'outline' | 'mint' | 'warning' | 'danger';

const toneClasses: Record<BadgeTone, string> = {
  outline: 'border border-line text-ink bg-paper',
  mint: 'border border-mint bg-mint text-mint-ink',
  warning: 'border border-warning bg-warning-bg text-warning',
  danger: 'border border-danger bg-paper text-danger',
};

/** A small pill label — vervangt `.tag`. */
export function Badge({
  tone = 'outline',
  wrap = false,
  children,
  className,
}: {
  tone?: BadgeTone;
  /** Set when the content might not fit on one line (e.g. a longer label
   * used as a narrow `StatTile`'s value) — trades the fixed-height, fully
   * rounded pill for a `rounded-xl` shape with extra vertical padding, so
   * text that wraps to two lines never crowds the rounded corners. Default
   * `false` keeps every existing single-line badge pixel-identical. */
  wrap?: boolean;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center text-xs font-medium',
        wrap ? 'rounded-xl px-2.5 py-1 leading-snug' : 'rounded-pill h-6 px-2.5',
        toneClasses[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}
